import { useState, useCallback, useEffect } from 'react';
import { getCurrentLocale } from 'utils';

/**
 * useOnboardingFormData - Hook for managing onboarding form state
 *
 * Handles:
 * - Form data state management
 * - Field updates
 * - Backend API saving
 * - Error handling
 */
export function useOnboardingFormData(initialData = {}) {
  const currentLocale = getCurrentLocale();

  // Initialize form data with defaults and any existing data
  const [formData, setFormData] = useState(() => ({
    // Step 1: Basic Info
    display_name: initialData.user?.display_name || '',

    // Step 2: Teaching Subjects
    teaching_subjects: initialData.teacher_profile?.teaching_subjects || [],
    subject_tags: initialData.teacher_profile?.subject_tags || {},

    // Step 3: Teaching Languages
    teaching_languages: initialData.teacher_profile?.teaching_languages || [],

    // Step 4: About You (Bio + Social Links)
    bio: initialData.teacher_profile?.bio || '',
    links: initialData.teacher_profile?.links || [],

    // Step 5: Schedule
    // Treat "UTC" as empty since it's the database default, not a user choice
    timezone: (initialData.user?.timezone && initialData.user.timezone !== 'UTC') ? initialData.user.timezone : '',
    schedules: initialData.teacher_profile?.schedules || []
  }));

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Sync formData when initialData is loaded from backend
  // This handles the case where initialData is empty on mount but populated later
  useEffect(() => {
    // Only sync if we have real data from backend (check for email which always exists)
    if (initialData.user?.email) {
      setFormData(prev => ({
        // Step 1: Basic Info - use backend data if form is empty
        display_name: prev.display_name || initialData.user?.display_name || '',

        // Step 2: Teaching Subjects
        teaching_subjects: prev.teaching_subjects?.length > 0
          ? prev.teaching_subjects
          : (initialData.teacher_profile?.teaching_subjects || []),
        subject_tags: Object.keys(prev.subject_tags || {}).length > 0
          ? prev.subject_tags
          : (initialData.teacher_profile?.subject_tags || {}),

        // Step 3: Teaching Languages
        teaching_languages: prev.teaching_languages?.length > 0
          ? prev.teaching_languages
          : (initialData.teacher_profile?.teaching_languages || []),

        // Step 4: About You (Bio + Social Links)
        bio: prev.bio || initialData.teacher_profile?.bio || '',
        links: prev.links?.length > 0
          ? prev.links
          : (initialData.teacher_profile?.links || []),

        // Step 5: Schedule
        // Note: schedules are NOT synced from backend - if step is shown, user starts fresh
        // Treat "UTC" as empty since it's the database default, not a user choice
        timezone: prev.timezone || ((initialData.user?.timezone && initialData.user.timezone !== 'UTC') ? initialData.user.timezone : ''),
        schedules: prev.schedules || []
      }));
    }
  }, [initialData.user?.email, initialData.user?.display_name, currentLocale]);

  // Update a single field
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setSaveError(null);
  }, []);

  // Save current form data to backend
  const saveToBackend = useCallback(async (stepId) => {
    setIsSaving(true);
    setSaveError(null);

    try {
      // Prepare payload based on what fields we need to save
      const userPayload = {};
      const profilePayload = {};

      // Always include basic user fields
      if (formData.display_name) userPayload.display_name = formData.display_name;
      if (formData.timezone) userPayload.timezone = formData.timezone;

      // Teaching settings
      if (formData.teaching_subjects?.length > 0) {
        profilePayload.teaching_subjects = formData.teaching_subjects;
      }
      if (Object.keys(formData.subject_tags || {}).length > 0) {
        profilePayload.subject_tags = formData.subject_tags;
      }
      if (formData.teaching_languages?.length > 0) {
        profilePayload.teaching_languages = formData.teaching_languages;
      }

      // Note: Social links are saved via PagesController#update_block, not here

      // Save user and profile data via main profile endpoint
      // Pass onboarding_step to enable step-based validation (only validates fields for this step)
      const response = await fetch('/dashboard/profile', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({
          onboarding_step: stepId,
          user: userPayload,
          teacher_profile: profilePayload
        })
      });

      if (!response.ok) {
        const data = await response.json();
        // API 返回格式: { status: "error", errors: ["..."] }
        if (data.errors && Array.isArray(data.errors)) {
          const fieldErrors = {};
          data.errors.forEach(error => {
            const lowerError = error.toLowerCase();
            if (lowerError.includes('display') || lowerError.includes('顯示名稱')) {
              fieldErrors.display_name = error;
            } else {
              fieldErrors._general = error;
            }
          });
          const apiError = new Error('API validation failed');
          apiError.fieldErrors = fieldErrors;
          throw apiError;
        }
        throw new Error(data.message || 'Failed to save profile data');
      }

      // Save bio to language content if present
      if (formData.bio) {
        const bioResponse = await fetch('/dashboard/page/update_language_content', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            language: currentLocale,
            bio: formData.bio,
            onboarding_step: stepId
          })
        });

        if (!bioResponse.ok) {
          throw new Error('Failed to save bio');
        }
      }

      // Save links via page controller if present
      if (formData.links?.length > 0) {
        const linksResponse = await fetch('/dashboard/page/update_block', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            block_type: 'links',
            content: formData.links,
            onboarding_step: stepId
          })
        });

        if (!linksResponse.ok) {
          throw new Error('Failed to save links');
        }
      }

      // Save schedules unconditionally if present
      if (formData.schedules?.length > 0) {
        const scheduleData = formData.schedules[0];
        // Use schedule ID from formData, or fall back to default_schedule_id from backend
        const scheduleId = scheduleData.id || initialData.teacher_profile?.default_schedule_id;

        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
        const schedulePayload = { schedule: scheduleData };

        let scheduleResponse;

        // Try PUT to update existing schedule if we have an ID
        if (scheduleId) {
          scheduleResponse = await fetch(`/dashboard/schedules/${scheduleId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
            body: JSON.stringify(schedulePayload)
          });

          // If schedule not found (404), fallback to POST to create new one
          if (scheduleResponse.status === 404) {
            scheduleResponse = await fetch('/dashboard/schedules', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
              body: JSON.stringify(schedulePayload)
            });
          }
        } else {
          // No existing schedule, create new one
          scheduleResponse = await fetch('/dashboard/schedules', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
            body: JSON.stringify(schedulePayload)
          });
        }

        if (!scheduleResponse.ok) {
          const errorData = await scheduleResponse.json().catch(() => ({}));
          console.error('Schedule save failed:', scheduleResponse.status, errorData);
          throw new Error(errorData.errors?.join(', ') || 'Failed to save schedule');
        }
      }

      setIsSaving(false);
      return true;
    } catch (error) {
      console.error('Save error:', error);
      setSaveError(error.message);
      setIsSaving(false);
      throw error;
    }
  }, [formData, currentLocale]);

  return {
    formData,
    updateField,
    saveToBackend,
    isSaving,
    saveError
  };
}

export default useOnboardingFormData;
